import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { API } from "../App";
import { authFetch, clearSessionToken } from "../utils/auth";

/**
 * Auth gate for private routes.
 *  - Redirects to /login (with returnTo state) synchronously if no session_token exists.
 *  - Validates the token against /api/auth/me before rendering children.
 *  - Clears stale tokens so the nav ("My Vault", etc.) doesn't linger after logout / expiry.
 */
export default function ProtectedRoute({ children }) {
  const location = useLocation();
  const hasToken = !!localStorage.getItem("session_token");
  const [status, setStatus] = useState(hasToken ? "checking" : "unauthorized");

  useEffect(() => {
    if (!hasToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/auth/me`);
        if (cancelled) return;
        if (res.ok) {
          setStatus("authorized");
        } else if (res.status === 401 || res.status === 403) {
          // Definitive auth failure — token is invalid/expired.
          clearSessionToken();
          setStatus("unauthorized");
        } else {
          // Transient server/network error — do NOT clear the token; let the user in optimistically.
          setStatus("authorized");
        }
      } catch {
        if (cancelled) return;
        // Network error / aborted fetch — do NOT clear the token, keep the user signed in.
        setStatus("authorized");
      }
    })();
    return () => { cancelled = true; };
  }, [hasToken]);

  if (status === "unauthorized") {
    return (
      <Navigate
        to="/login"
        state={{ returnTo: location.pathname + location.search }}
        replace
      />
    );
  }

  if (status === "checking") {
    return (
      <div
        className="min-h-screen bg-[#020617] flex items-center justify-center"
        data-testid="protected-route-loading"
      >
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return children;
}
